import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, chakra, Separator } from '@chakra-ui/react';
import { presets, typographyStyles } from '@src/components/sco/MarkdownRenderer/typographyStyles';
import { Wrapper } from '@src/components/sco/RichMarkdownInput/components/EditorWrapper';
import { Element } from '@src/components/sco/RichMarkdownInput/components/Element';
import { Leaf } from '@src/components/sco/RichMarkdownInput/components/Leaf';
import { LinkModal } from '@src/components/sco/RichMarkdownInput/components/LinkModal';
import { Toolbar } from '@src/components/sco/RichMarkdownInput/components/Toolbar';
import { LinkModalProvider, useLinkModal } from '@src/components/sco/RichMarkdownInput/context/LinkModalContext';
import { withEmojis } from '@src/components/sco/RichMarkdownInput/plugins/emojis';
import { withGiphy } from '@src/components/sco/RichMarkdownInput/plugins/giphy';
import { withImages } from '@src/components/sco/RichMarkdownInput/plugins/images';
import { withLineBreaks } from '@src/components/sco/RichMarkdownInput/plugins/lineBreaks';
import { withLinks } from '@src/components/sco/RichMarkdownInput/plugins/links';
import { linkifyNodes } from '@src/components/sco/RichMarkdownInput/plugins/links/linkify';
import { withLists } from '@src/components/sco/RichMarkdownInput/plugins/lists';
import { withMarkHotKeys } from '@src/components/sco/RichMarkdownInput/plugins/markHotKeys';
import { withMarkdownDetection } from '@src/components/sco/RichMarkdownInput/plugins/markdownDetection';
import { DEFAULT_MENTION_STATE, Mentionable, withMentions } from '@src/components/sco/RichMarkdownInput/plugins/mentions';
import { MentionSelector, MentionState } from '@src/components/sco/RichMarkdownInput/plugins/mentions/MentionSelector';
import { withRawMarkdown } from '@src/components/sco/RichMarkdownInput/plugins/rawMarkdown';
import { withShortcuts } from '@src/components/sco/RichMarkdownInput/plugins/shortcuts';
import { setGlobalOpenLinkModal } from '@src/components/sco/RichMarkdownInput/utilities/blocks';
import { deserializeNodesFromMarkdown, serializeNodesToMarkdown } from '@src/components/sco/RichMarkdownInput/utilities/markdown';
import { PrettyPrintJSON } from '@src/components/sco/utilities/PrettyPrintJSON';
import React, { Dispatch, ReactNode, SetStateAction, useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from 'react';
import { BaseElement, createEditor, Editor, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, Slate, useSlate, withReact } from 'slate-react';
export type RichMarkdownInputValues = {
  markdown?: string;
  slate?: SlateNode[];
};
export type SlateEditor = ReactEditor & {
  onKeyDown: (editor: SlateEditor, event: KeyboardEvent) => void;
  insertMention?: (editor: SlateEditor, mentionable: Mentionable) => void;
  insertLink?: (url: string, label?: string) => void;
  mentionState?: MentionState;
  setMentionState?: Dispatch<SetStateAction<MentionState>>;
  /** Hands the editor the latest mention state (RichMarkdownInput's layout effect). */
  syncMentionState?: (next: MentionState) => void;
};
export type SlateNode = BaseElement & {
  type?: string;
  text?: string;
  children?: SlateNode[];
};
export function createSlateEditor(props: any = {}): SlateEditor {
  let editor: any = createEditor() as ReactEditor;
  editor = withHistory(editor);
  editor = withReact(editor);
  editor = withMarkHotKeys(editor, props.pluginConfigs?.hotKeys);
  editor = withMarkdownDetection(editor, props.pluginConfigs?.markdown);
  editor = withLinks(editor, props.pluginConfigs?.links);
  editor = withLists(editor, props.pluginConfigs?.lists);
  editor = withLineBreaks(editor);
  editor = withMentions(editor, props.pluginConfigs?.mentions);
  editor = withEmojis(editor, props.pluginConfigs?.emojis);
  editor = withImages(editor, props.pluginConfigs?.images);
  editor = withGiphy(editor, props.pluginConfigs?.giphy);
  editor = withRawMarkdown(editor);
  editor = withShortcuts(editor, props.pluginConfigs?.shortcuts);
  return editor;
}

// A fresh, empty document. Always create a NEW object graph (never reuse a
// shared constant) — see `normalizeSlateValue` for why.
const createDefaultValue = (): SlateNode[] => [{
  type: 'paragraph',
  children: [{
    text: ''
  }]
}];

// Slate nodes are plain JSON-serializable data, so a structured round-trip is a
// safe, dependency-free deep clone.
const cloneSlateValue = (value: SlateNode[]): SlateNode[] => JSON.parse(JSON.stringify(value));

/**
 * Slate requires the editor's top-level value to be a non-empty array of
 * element nodes (each with a `children` array). When the incoming value is
 * empty, null-ish, or contains bare text nodes at the top level, Slate throws
 * `Unable to find the path for Slate node: {"text":""}` on mount. Normalize
 * defensively so the editor always receives a valid document.
 *
 * Critically, the returned value is always a fresh, deep-cloned object graph.
 * slate-react tracks nodes in WeakMaps keyed by object identity
 * (`NODE_TO_PARENT` / `NODE_TO_INDEX`). If two editor instances were seeded
 * with the same node objects (e.g. a shared default value), the second editor
 * to mount would clobber those map entries, and the first editor's
 * `findPath()` would walk into the wrong tree and throw
 * `Unable to find the path for Slate node: {"text":""}`. Cloning guarantees
 * every editor owns its nodes.
 */
export const normalizeSlateValue = (value: any): SlateNode[] => {
  if (!Array.isArray(value) || value.length === 0) {
    return createDefaultValue();
  }
  const normalized = value.filter((node: any) => node && typeof node === 'object').map((node: any) => {
    // A valid top-level node is an element with a `children` array.
    if (Array.isArray(node.children)) {
      return node;
    }
    // Bare text nodes at the top level are invalid — wrap them in a paragraph.
    if (typeof node.text === 'string') {
      return {
        type: 'paragraph',
        children: [node]
      };
    }
    // Element missing its children — give it an empty text node.
    return {
      ...node,
      type: node.type || 'paragraph',
      children: [{
        text: ''
      }]
    };
  });

  // Deep-clone so the editor never shares node identities with another editor
  // or with the caller's source array.
  return normalized.length ? cloneSlateValue(normalized) : createDefaultValue();
};

/** The editor styled like the `MarkdownRenderer` preset its text is shown with (`ugc`, `chat-message`, …). */
export const editorTypographyStyles = (preset: string) => typographyStyles(presets[preset]?.typographyStyles || {});
const defaultTypographyStyles = editorTypographyStyles('ugc');

/** Cap the self-growing editor. Viewport units are resolved to px on resize. */
export const DEFAULT_EDITOR_MAX_HEIGHT = '60vh';
const VIEWPORT_HEIGHT_UNIT = /^(\d+(?:\.\d+)?)(dvh|svh|lvh|vh)$/i;
const resolveMaxHeight = (maxHeight: string, viewportHeight: number) => {
  const match = String(maxHeight).trim().match(VIEWPORT_HEIGHT_UNIT);
  if (!match) {
    return maxHeight;
  }
  return `${Math.round(parseFloat(match[1]) / 100 * viewportHeight)}px`;
};
const useResolvedMaxHeight = maxHeight => {
  const $ = _c(3);
  const [resolved, setResolved] = useState(maxHeight);
  let t0;
  let t1;
  if ($[0] !== maxHeight) {
    t0 = () => {
      const update = () => {
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        setResolved(resolveMaxHeight(maxHeight, viewportHeight));
      };
      update();
      window.addEventListener("resize", update);
      window.visualViewport?.addEventListener("resize", update);
      return () => {
        window.removeEventListener("resize", update);
        window.visualViewport?.removeEventListener("resize", update);
      };
    };
    t1 = [maxHeight];
    $[0] = maxHeight;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return resolved;
};
export type RichMarkdownInputProps = {
  disabled?: boolean;
  autoFocus?: boolean;
  boxProps?: BoxProps;
  slateBoxProps?: BoxProps;
  initialMarkdownValue?: any;
  initialSlateValue?: any;
  onBlur?: (event: any) => void;
  onChange?: (values: RichMarkdownInputValues) => void;
  onFocus?: (event: any) => void;
  onKeyDown?: (event: any) => void;
  placeholder?: any;
  printDebug?: boolean;
  minHeight?: string;
  /** Max height of the self-growing editor. Defaults to 60vh (resolved to px). Pass `none` to disable. */
  maxHeight?: string;
  additionalToolbarButtons?: ReactNode[] | ReactNode;
  toolbarPlacement?: 'top' | 'bottom';
  typographyStyles?: any;
  /** When true, shows Attach file next to Link on the formatting toolbar */
  enableFileAttachments?: boolean;
  fileAttachmentsDisabled?: boolean;
  /** Called when the user picks a file from the Attach toolbar control */
  onFileAttachmentSelected?: (file: File) => void | Promise<void>;
  pluginConfigs?: {
    emojis?: any;
    giphy?: any;
    hotKeys?: any;
    images?: any;
    links?: any;
    lists?: any;
    markdown?: any;
    mentions?: any;
    shortcuts?: any;
  };
};
export const RichMarkdownInput = props => {
  const $ = _c(89);
  const [mentionState, setMentionState] = useState(DEFAULT_MENTION_STATE);
  let t0;
  if ($[0] !== props) {
    t0 = () => {
      const created = createSlateEditor(props);
      let latestMentionState = DEFAULT_MENTION_STATE;
      Object.defineProperty(created, "mentionState", {
        configurable: true,
        get: () => latestMentionState
      });
      created.syncMentionState = next => {
        latestMentionState = next;
      };
      created.setMentionState = setMentionState;
      return created;
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [editor] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== editor || $[3] !== mentionState) {
    t1 = () => {
      editor.syncMentionState?.(mentionState);
    };
    t2 = [editor, mentionState];
    $[2] = editor;
    $[3] = mentionState;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useLayoutEffect(t1, t2);
  const lastSelection = useRef(editor.selection);
  const fileInputRef = useRef(null);
  const resolvedMaxHeight = useResolvedMaxHeight(props.maxHeight ?? DEFAULT_EDITOR_MAX_HEIGHT);
  let t3;
  if ($[6] !== props.fileAttachmentsDisabled) {
    t3 = () => {
      if (props.fileAttachmentsDisabled) {
        return;
      }
      fileInputRef.current?.click();
    };
    $[6] = props.fileAttachmentsDisabled;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const openFileAttachmentPicker = t3;
  let t4;
  if ($[8] !== props) {
    t4 = async e => {
      const file = e.target.files?.[0];
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (!file) {
        return;
      }
      await props.onFileAttachmentSelected?.(file);
    };
    $[8] = props;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const onFileInputChange = t4;
  let t5;
  if ($[10] !== props.initialMarkdownValue || $[11] !== props.initialSlateValue) {
    t5 = () => normalizeSlateValue(props.initialSlateValue ?? (props.initialMarkdownValue ? deserializeNodesFromMarkdown(props.initialMarkdownValue) : undefined));
    $[10] = props.initialMarkdownValue;
    $[11] = props.initialSlateValue;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const [initialSlateValue] = useState(t5);
  const markdownRef = useRef(props.initialMarkdownValue || "");
  const editedRef = useRef(false);
  let t6;
  if ($[13] !== initialSlateValue || $[14] !== props.initialMarkdownValue) {
    t6 = () => ({
      slate: initialSlateValue,
      markdown: props.initialMarkdownValue || ""
    });
    $[13] = initialSlateValue;
    $[14] = props.initialMarkdownValue;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const [debugValues, setDebugValues] = useState(t6);
  const [isFocused, setIsFocused] = useState(false);
  let t7;
  if ($[16] !== initialSlateValue || $[17] !== props) {
    t7 = () => {
      props.onChange?.({
        slate: initialSlateValue,
        markdown: markdownRef.current
      });
    };
    $[16] = initialSlateValue;
    $[17] = props;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const reportInitialValue = useEffectEvent(t7);
  let t8;
  if ($[19] !== reportInitialValue) {
    t8 = () => {
      reportInitialValue();
    };
    $[19] = reportInitialValue;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  useEffect(t8, t9);
  let t10;
  if ($[22] !== editor.operations || $[23] !== props) {
    t10 = newSlateValue => {
      if (newSlateValue?.mentionState) {
        setMentionState(newSlateValue.mentionState);
        return;
      }
      if (editor.operations.some(_temp)) {
        editedRef.current = true;
      }
      if (!editedRef.current) {
        return;
      }
      const markdown = serializeNodesToMarkdown(props.pluginConfigs?.links?.disabled ? newSlateValue : linkifyNodes(newSlateValue));
      if (markdown === markdownRef.current) {
        return;
      }
      markdownRef.current = markdown;
      if (props.printDebug) {
        setDebugValues({
          slate: newSlateValue,
          markdown
        });
      }
      props.onChange?.({
        slate: newSlateValue,
        markdown
      });
    };
    $[22] = editor.operations;
    $[23] = props;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  let t12;
  let t13;
  if ($[25] !== editor || $[26] !== props) {
    t11 = event => {
      setIsFocused(true);
      ReactEditor.focus(editor);
      if (!editor.selection) {
        Transforms.select(editor, lastSelection.current ?? Editor.end(editor, []));
      }
      if (props.onFocus) {
        props.onFocus(event);
      }
    };
    t12 = event_0 => {
      setIsFocused(false);
      ReactEditor.blur(editor);
      if (props.onBlur) {
        props.onBlur(event_0);
      }
    };
    t13 = event_1 => {
      ;
      try {
        if (editor.onKeyDown) {
          editor.onKeyDown(editor, event_1);
        }
        if (props.onKeyDown) {
          props.onKeyDown(event_1);
        }
      } catch (t14) {
        const err = t14;
        console.log({
          err
        });
      }
    };
    $[25] = editor;
    $[26] = props;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t11 = $[27];
    t12 = $[28];
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t13) {
    t14 = {
      onChange: t10,
      onFocus: t11,
      onBlur: t12,
      onKeyDown: t13
    };
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const handlers = t14;
  const t15 = props.typographyStyles || defaultTypographyStyles;
  let t16;
  if ($[35] !== openFileAttachmentPicker || $[36] !== props.additionalToolbarButtons || $[37] !== props.enableFileAttachments || $[38] !== props.fileAttachmentsDisabled || $[39] !== props.toolbarPlacement) {
    t16 = props.toolbarPlacement === "top" && <><Toolbar additionalButtons={props.additionalToolbarButtons} enableFileAttachments={props.enableFileAttachments} fileAttachmentsDisabled={props.fileAttachmentsDisabled} placement={props.toolbarPlacement} onAttachFileClick={openFileAttachmentPicker} /></>;
    $[35] = openFileAttachmentPicker;
    $[36] = props.additionalToolbarButtons;
    $[37] = props.enableFileAttachments;
    $[38] = props.fileAttachmentsDisabled;
    $[39] = props.toolbarPlacement;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== editor) {
    t17 = event_2 => ReactEditor.focus(editor);
    $[41] = editor;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const t18 = props.disabled || false;
  let t19;
  if ($[43] !== props.minHeight || $[44] !== resolvedMaxHeight) {
    t19 = {
      minHeight: props.minHeight,
      maxHeight: resolvedMaxHeight,
      resize: "vertical",
      outline: "none",
      padding: "1em",
      overflow: "auto"
    };
    $[43] = props.minHeight;
    $[44] = resolvedMaxHeight;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== handlers.onBlur || $[47] !== handlers.onFocus || $[48] !== handlers.onKeyDown || $[49] !== props.autoFocus || $[50] !== props.placeholder || $[51] !== t18 || $[52] !== t19) {
    t20 = <Editable autoFocus={props.autoFocus} placeholder={props.placeholder} readOnly={t18} renderElement={Element} renderLeaf={Leaf} renderPlaceholder={_temp2} style={t19} onBlur={handlers.onBlur} onFocus={handlers.onFocus} onKeyDown={handlers.onKeyDown} />;
    $[46] = handlers.onBlur;
    $[47] = handlers.onFocus;
    $[48] = handlers.onKeyDown;
    $[49] = props.autoFocus;
    $[50] = props.placeholder;
    $[51] = t18;
    $[52] = t19;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== props.slateBoxProps || $[55] !== t17 || $[56] !== t20) {
    t21 = <Box fontFamily="longform" padding={0} onClick={t17} {...props.slateBoxProps}>{t20}</Box>;
    $[54] = props.slateBoxProps;
    $[55] = t17;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== openFileAttachmentPicker || $[59] !== props.additionalToolbarButtons || $[60] !== props.enableFileAttachments || $[61] !== props.fileAttachmentsDisabled || $[62] !== props.toolbarPlacement) {
    t22 = props.toolbarPlacement !== "top" && <><Toolbar additionalButtons={props.additionalToolbarButtons} enableFileAttachments={props.enableFileAttachments} fileAttachmentsDisabled={props.fileAttachmentsDisabled} placement={props.toolbarPlacement} onAttachFileClick={openFileAttachmentPicker} /></>;
    $[58] = openFileAttachmentPicker;
    $[59] = props.additionalToolbarButtons;
    $[60] = props.enableFileAttachments;
    $[61] = props.fileAttachmentsDisabled;
    $[62] = props.toolbarPlacement;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== mentionState) {
    t23 = mentionState.target && <MentionSelector mentionState={mentionState} setMentionState={setMentionState} />;
    $[64] = mentionState;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== editor || $[67] !== handlers.onChange || $[68] !== initialSlateValue || $[69] !== t16 || $[70] !== t21 || $[71] !== t22 || $[72] !== t23) {
    t24 = <Slate editor={editor} initialValue={initialSlateValue} onChange={handlers.onChange}>{t16}{t21}{t22}{t23}</Slate>;
    $[66] = editor;
    $[67] = handlers.onChange;
    $[68] = initialSlateValue;
    $[69] = t16;
    $[70] = t21;
    $[71] = t22;
    $[72] = t23;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== onFileInputChange || $[75] !== props.enableFileAttachments) {
    t25 = props.enableFileAttachments && <input ref={fileInputRef} style={{
      display: "none"
    }} type="file" onChange={onFileInputChange} />;
    $[74] = onFileInputChange;
    $[75] = props.enableFileAttachments;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== debugValues || $[78] !== mentionState || $[79] !== props.printDebug) {
    t26 = props.printDebug && <><Separator /><PrettyPrintJSON data={{
        mentionState,
        slateValue: debugValues.slate,
        markdownValue: debugValues.markdown
      }} /></>;
    $[77] = debugValues;
    $[78] = mentionState;
    $[79] = props.printDebug;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <LinkModalWithContext />;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== isFocused || $[83] !== props.boxProps || $[84] !== t15 || $[85] !== t24 || $[86] !== t25 || $[87] !== t26) {
    t28 = <LinkModalProvider><Wrapper boxProps={props.boxProps} isFocused={isFocused} typographyStyles={t15}>{t24}{t25}{t26}{t27}</Wrapper></LinkModalProvider>;
    $[82] = isFocused;
    $[83] = props.boxProps;
    $[84] = t15;
    $[85] = t24;
    $[86] = t25;
    $[87] = t26;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  return t28;
};
const LinkModalWithContext = () => {
  const $ = _c(11);
  const {
    isOpen,
    initialUrl,
    initialLabel,
    title,
    onSubmit,
    onRemove,
    closeLinkModal,
    openLinkModal
  } = useLinkModal();
  let t0;
  let t1;
  if ($[0] !== openLinkModal) {
    t0 = () => {
      setGlobalOpenLinkModal(openLinkModal);
      return _temp3;
    };
    t1 = [openLinkModal];
    $[0] = openLinkModal;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== closeLinkModal || $[4] !== initialLabel || $[5] !== initialUrl || $[6] !== isOpen || $[7] !== onRemove || $[8] !== onSubmit || $[9] !== title) {
    t2 = <LinkModal initialLabel={initialLabel} initialUrl={initialUrl} isOpen={isOpen} title={title} onClose={closeLinkModal} onRemove={onRemove} onSubmit={onSubmit} />;
    $[3] = closeLinkModal;
    $[4] = initialLabel;
    $[5] = initialUrl;
    $[6] = isOpen;
    $[7] = onRemove;
    $[8] = onSubmit;
    $[9] = title;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
const Placeholder = props => {
  const $ = _c(8);
  const editor = useSlate() as ReactEditor;
  if (editor.selection) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <></>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      opacity: 0.5,
      position: "absolute",
      display: "inline-block",
      marginTop: "-24px"
    };
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== editor) {
    t1 = event => {
      event.preventDefault();
      ReactEditor.focus(editor);
    };
    $[2] = editor;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.attributes || $[5] !== props.children || $[6] !== t1) {
    t2 = <chakra.span color="colorPalette.fg/50" {...props.attributes} style={t0} onMouseDown={t1}>{props.children}</chakra.span>;
    $[4] = props.attributes;
    $[5] = props.children;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
function _temp(op) {
  return op.type !== "set_selection";
}
function _temp2(props_0) {
  return <Placeholder {...props_0} />;
}
function _temp3() {
  setGlobalOpenLinkModal(null);
}